import { getPageLayoutDefinition, isWebtoonLayout } from './comicPageLayout'
import { ensureEpisodePages, getPanelsOnPage } from './comicPages'
import type { ComicEpisode, ComicPage, ComicProject } from './comicTypes'

export type ComicReaderViewMode = 'scroll' | 'paginate'

export type ComicReaderPageEntry = {
  key: string
  episodeId: string
  episodeTitle: string
  episodeIndex: number
  page: ComicPage
  pageIndexInEpisode: number
}

export function pageHasReaderContent(episode: ComicEpisode, page: ComicPage): boolean {
  if (page.pageImageUrl?.trim()) return true
  return getPanelsOnPage(episode, page).some((p) => Boolean(p.imageUrl?.trim()))
}

/** 阅读器缩略图：整页图优先，否则取首格有图 URL */
export function readerEntryThumbUrl(episode: ComicEpisode, page: ComicPage): string | null {
  const whole = page.pageImageUrl?.trim()
  if (whole) return whole
  for (const panel of getPanelsOnPage(episode, page)) {
    const url = panel.imageUrl?.trim()
    if (url) return url
  }
  return null
}

export function pageLayoutLabel(layout: ComicPage['layout']): string {
  return getPageLayoutDefinition(layout).label
}

export function buildReaderPageEntries(project: ComicProject): ComicReaderPageEntry[] {
  const entries: ComicReaderPageEntry[] = []
  project.episodes.forEach((rawEpisode, episodeIndex) => {
    const episode = ensureEpisodePages(rawEpisode)
    episode.pages.forEach((page, pageIndexInEpisode) => {
      entries.push({
        key: `${episode.id}:${page.id}`,
        episodeId: episode.id,
        episodeTitle: episode.title,
        episodeIndex,
        page,
        pageIndexInEpisode,
      })
    })
  })
  return entries
}

export function buildReaderEpisodeMap(project: ComicProject): Map<string, ComicEpisode> {
  const map = new Map<string, ComicEpisode>()
  for (const raw of project.episodes) {
    const episode = ensureEpisodePages(raw)
    map.set(episode.id, episode)
  }
  return map
}

export function filterReaderEntries(
  entries: ComicReaderPageEntry[],
  episodesById: Map<string, ComicEpisode>,
  opts: { onlyWithContent?: boolean; webtoonOnly?: boolean },
): ComicReaderPageEntry[] {
  return entries.filter((entry) => {
    if (opts.webtoonOnly && !isWebtoonLayout(entry.page.layout)) return false
    if (!opts.onlyWithContent) return true
    const episode = episodesById.get(entry.episodeId)
    if (!episode) return false
    return pageHasReaderContent(episode, entry.page)
  })
}

export function readerProgressLabel(index: number, total: number): string {
  if (total <= 0) return '0 / 0'
  return `${index + 1} / ${total}`
}

export type ComicReaderEpisodeSummary = {
  episodeId: string
  episodeTitle: string
  episodeIndex: number
  /** 当前筛选下该话可见页数 */
  pageCount: number
  /** 跳转到该话时使用：该话在 entries 中的首条 key */
  firstEntryKey: string
}

/** 阅读话次目录（小说章节式，仅话、不含分页） */
export function buildReaderEpisodeSummaries(
  entries: ComicReaderPageEntry[],
): ComicReaderEpisodeSummary[] {
  const summaries: ComicReaderEpisodeSummary[] = []
  const seen = new Set<string>()
  for (const entry of entries) {
    if (seen.has(entry.episodeId)) continue
    seen.add(entry.episodeId)
    summaries.push({
      episodeId: entry.episodeId,
      episodeTitle: entry.episodeTitle,
      episodeIndex: entry.episodeIndex,
      pageCount: entries.filter((e) => e.episodeId === entry.episodeId).length,
      firstEntryKey: entry.key,
    })
  }
  return summaries
}

export function firstEntryIndexForEpisode(
  entries: ComicReaderPageEntry[],
  episodeId: string,
): number {
  return entries.findIndex((e) => e.episodeId === episodeId)
}
